import { useState } from "react";
import { Pencil, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

export function EditReplyButton({ c, onEdit }) {
  const { user, isAdmin } = useAuth();
  if (!(isAdmin || c.created_by === user?.id)) return null;
  return (
    <button onClick={onEdit} className="text-slate-300 hover:text-slate-700 transition-colors" aria-label="Edit reply" data-testid={`edit-reply-${c.id}`}>
      <Pencil className="h-3.5 w-3.5" />
    </button>
  );
}

export function DeleteReplyButton({ c, endpoint, reload }) {
  const { user, isAdmin } = useAuth();
  if (!(isAdmin || c.created_by === user?.id)) return null;
  const remove = async () => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      await api.delete(endpoint);
      toast.success("Reply deleted");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <button onClick={remove} className="text-slate-300 hover:text-red-500 transition-colors" aria-label="Delete reply" data-testid={`delete-reply-${c.id}`}>
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
}

export function ReplyBody({ c, endpoint, editing, setEditing, reload }) {
  const [text, setText] = useState(c.body || "");
  const [saving, setSaving] = useState(false);

  if (!editing) return c.body ? <p className="text-sm text-slate-600 mt-0.5 whitespace-pre-wrap">{c.body}</p> : null;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(endpoint, { body: text });
      toast.success("Reply updated");
      setEditing(false);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-1 space-y-2" data-testid={`edit-reply-form-${c.id}`}>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} autoFocus data-testid={`edit-reply-input-${c.id}`} />
      <div className="flex gap-2">
        <Button size="sm" onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-700 text-white" data-testid={`edit-reply-save-${c.id}`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => { setText(c.body || ""); setEditing(false); }} data-testid={`edit-reply-cancel-${c.id}`}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export function Reply({ c, endpoint, reload, header, children }) {
  const [editing, setEditing] = useState(false);
  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        {header}
        {c.edited_at && <span className="text-[11px] italic text-slate-400" data-testid={`reply-edited-${c.id}`}>edited</span>}
        {!editing && <EditReplyButton c={c} onEdit={() => setEditing(true)} />}
        {!editing && <DeleteReplyButton c={c} endpoint={endpoint} reload={reload} />}
      </div>
      <ReplyBody c={c} endpoint={endpoint} editing={editing} setEditing={setEditing} reload={reload} />
      {children}
    </>
  );
}
